/**
 * Зведені журнали «Комерції» (ч.1216, рішення власника: «рівно так, як це влаштовано в 1С»):
 *   • «Зведена реалізацій» — журнал 1С «Документи покупців»;
 *   • «Зведена надходжень» — журнал 1С «Документи постачальників».
 * Одна таблиця з УСІМА документами розділу (рахунки, замовлення, реалізації / надходження,
 * повернення, платіжні документи за розрахунками з контрагентом, податкові накладні) і
 * колонками журналу 1С: Дата · Номер · Вид документа · Сума · Валюта · Контрагент · Договір
 * контрагента · Організація · Відповідальний · Коментар. Фільтри рядком: організація,
 * період, контрагент, вид документа, номер. Клік по рядку показує попап зі звʼязаними
 * документами (ч.1217: той самий ланцюг, що кнопка «звʼязані» в редакторі), подвійний
 * клац або клік по номеру відкриває документ свого виду.
 *
 * Канон Норми #29: .card + InfiniteDocGrid (серверна пагінація і сортування) + фільтри
 * рядком. Види, на які в користувача немає права (банк / каса / податкові), сервер не
 * віддає і не показує в переліку «Вид документа».
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import {
  commerceJournal, type CommerceJournalKind, type CommerceJournalRow, type CommerceJournalSide,
} from '@/api/commerceJournal';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { RelatedDocsDialog } from '@/shared/components/RelatedDocs';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { contractLabel } from '@/shared/utils/labels';

/** Маршрут «відкрити документ» за видом журналу (коди — як у backend `commerce_journal`). */
export const COMMERCE_KIND_ROUTES: Record<string, (id: string) => string> = {
  // покупці
  customer_invoice: (id) => `/customer-invoices/${id}`,
  customer_order: (id) => `/customer-orders/${id}`,
  goods_sale: (id) => `/goods-sales/${id}`,
  service_act: (id) => `/service-acts/${id}`,
  production_service_act: (id) => `/production-service-acts/${id}`,
  tnn: (id) => `/tnn/${id}`,
  goods_return_from_customer: (id) => `/goods-returns-from-customer/${id}`,
  cash_receipt: (id) => `/cash-receipts/${id}`,
  tax_invoice: (id) => `/tax-invoices-outgoing/${id}`,
  tax_invoice_correction: (id) => `/tax-invoices-outgoing/${id}`,
  // постачальники
  supplier_invoice: (id) => `/supplier-invoices/${id}`,
  supplier_order: (id) => `/supplier-orders/${id}`,
  goods_receipt: (id) => `/goods-receipts/${id}`,
  additional_expenses: (id) => `/additional-expenses/${id}`,
  customs_declaration: (id) => `/customs-declarations/${id}`,
  goods_return_to_supplier: (id) => `/goods-returns-to-supplier/${id}`,
  cash_payment: (id) => `/cash-payments/${id}`,
  tax_invoice_incoming: (id) => `/tax-invoices-incoming/${id}`,
  tax_invoice_incoming_correction: (id) => `/tax-invoices-incoming/${id}`,
  power_of_attorney: (id) => `/settlements/powers-of-attorney/${id}`,
  // в обох журналах
  bank_receipt: (id) => `/bank-receipts/${id}`,
  bank_payment: (id) => `/bank-payments/${id}`,
};

interface SideConfig {
  title: string;
  subtitle: string;
  contractorLabel: string;
  storageKey: string;
  /** «+ Створити ▾» — як кнопка «Додати» журналу 1С. */
  create: { label: string; to: string }[];
}

export const COMMERCE_JOURNALS: Record<CommerceJournalSide, SideConfig> = {
  sales: {
    title: 'Зведена реалізацій',
    subtitle: 'усі документи покупців',
    contractorLabel: 'покупець',
    storageKey: 'commerce-journal-sales',
    create: [
      { label: 'Рахунок на оплату покупцю', to: '/customer-invoices/new' },
      { label: 'Замовлення покупця', to: '/customer-orders/new' },
      { label: 'Документ реалізації', to: '/goods-sales/new' },
      { label: 'Повернення товарів від покупця', to: '/goods-returns-from-customer/new' },
      { label: 'Податкова накладна', to: '/tax-invoices-outgoing/new' },
      { label: 'Платіжне доручення вхідне', to: '/bank-receipts/new' },
      { label: 'Прибутковий касовий ордер', to: '/cash-receipts/new' },
    ],
  },
  purchases: {
    title: 'Зведена надходжень',
    subtitle: 'усі документи постачальників',
    contractorLabel: 'постачальник',
    storageKey: 'commerce-journal-purchases',
    create: [
      { label: 'Рахунок на оплату постачальника', to: '/supplier-invoices/new' },
      { label: 'Замовлення постачальнику', to: '/supplier-orders/new' },
      { label: 'Надходження товарів і послуг', to: '/goods-receipts/new' },
      { label: 'Надходження додаткових витрат', to: '/additional-expenses/new' },
      { label: 'Повернення товарів постачальнику', to: '/goods-returns-to-supplier/new' },
      { label: 'Податкова накладна (вхідна)', to: '/tax-invoices-incoming/new' },
      { label: 'Платіжне доручення вихідне', to: '/bank-payments/new' },
      { label: 'Видатковий касовий ордер', to: '/cash-payments/new' },
    ],
  },
};

/** Подвійний клац складається з двох кліків: чекаємо стільки, перш ніж показати попап
 *  звʼязаних, щоб подвійний клац устиг відкрити документ, а не попап. */
export const ROW_CLICK_DELAY_MS = 260;

const fmtAmount = (v: string | null | undefined) => (v == null || v === ''
  ? '' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

/** Дата як у журналі 1С: «дд.мм.рррр гг:хх:сс»; у податкових накладних часу немає. */
export function journalDate(row: Pick<CommerceJournalRow, 'document_date' | 'has_time'>): string {
  if (!row.document_date) return '';
  if (!row.has_time) {
    const [y, m, d] = row.document_date.slice(0, 10).split('-');
    return `${d}.${m}.${y}`;
  }
  return new Date(row.document_date).toLocaleString('uk-UA');
}

/** Лише день документа (для підпису в попапі) — той самий день, що в колонці «Дата». */
export function journalDay(row: Pick<CommerceJournalRow, 'document_date' | 'has_time'>): string {
  if (!row.document_date) return '';
  return row.has_time ? new Date(row.document_date).toLocaleDateString('uk-UA') : journalDate(row);
}

export default function CommerceJournalPage({ side }: { side: CommerceJournalSide }) {
  const cfg = COMMERCE_JOURNALS[side];
  const navigate = useNavigate();
  const f = useDocListFilters();
  const [contractorId, setContractorId] = useState('');
  const [kind, setKind] = useState('');
  const [numberInput, setNumberInput] = useState('');
  const [number, setNumber] = useState('');
  const [kinds, setKinds] = useState<CommerceJournalKind[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  // ч.1217: рядок, для якого відкрито попап звʼязаних документів.
  const [related, setRelated] = useState<CommerceJournalRow | null>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingClick = () => {
    if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
  };
  useEffect(() => cancelPendingClick, []);

  // Пошук за номером — із затримкою, щоб не смикати сервер на кожну літеру.
  useEffect(() => {
    const t = setTimeout(() => setNumber(numberInput.trim()), 350);
    return () => clearTimeout(t);
  }, [numberInput]);
  // Інший журнал — свій перелік видів.
  useEffect(() => { setKind(''); setKinds([]); }, [side]);

  const { datasource, total } = useInfiniteGridDatasource<CommerceJournalRow>(
    async (limit, offset, sort) => {
      const page = await commerceJournal.list(side, {
        limit, offset,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        organization_id: f.orgId || undefined,
        contractor_id: contractorId || undefined,
        kind: kind ? [kind] : undefined,
        q: number || undefined,
        date_from: f.dateFrom, date_to: f.dateTo,
      });
      setKinds(page.kinds);
      return { items: page.items, total: page.total };
    },
    [side, contractorId, kind, number, ...f.queryKey],
  );

  const open = (row: CommerceJournalRow) => {
    cancelPendingClick();
    setRelated(null);
    const route = COMMERCE_KIND_ROUTES[row.kind];
    if (route) navigate(route(row.id));
  };
  const showRelated = (row: CommerceJournalRow) => {
    cancelPendingClick();
    clickTimer.current = setTimeout(() => { clickTimer.current = null; setRelated(row); }, ROW_CLICK_DELAY_MS);
  };

  const cols = useMemo<ColDef<CommerceJournalRow>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: CommerceJournalRow }) => <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'posted', headerName: 'Проведено', width: 110, sortable: true },
    {
      colId: 'date', field: 'document_date', headerName: 'Дата', width: 160, sortable: true,
      valueGetter: (p) => (p.data ? journalDate(p.data) : ''),
    },
    {
      colId: 'number', field: 'document_number', headerName: 'Номер', width: 140, sortable: true,
      cellRenderer: (p: { data?: CommerceJournalRow }) => (p.data
        ? <a style={{ cursor: 'pointer' }} title="Відкрити документ" onClick={() => open(p.data!)}>{p.data.document_number || 'без номера'}</a>
        : ''),
    },
    { colId: 'kind', field: 'kind_label', headerName: 'Вид документа', width: 230, sortable: true },
    {
      colId: 'amount', field: 'amount', headerName: 'Сума', width: 130, sortable: true,
      cellStyle: { textAlign: 'right' }, valueFormatter: (p) => fmtAmount(p.value),
    },
    {
      colId: 'currency', field: 'currency_code', headerName: 'Валюта', width: 80, sortable: true,
      cellStyle: { textAlign: 'center' },
      valueFormatter: (p) => (p.value === 'UAH' ? 'грн' : (p.value ?? '')),
    },
    {
      colId: 'contractor', field: 'contractor_name', headerName: 'Контрагент', flex: 1, minWidth: 200, sortable: true,
    },
    {
      colId: 'contract', field: 'contract_name', headerName: 'Договір контрагента', width: 210, sortable: true,
      valueGetter: (p) => (p.data?.contract_id
        ? contractLabel({ name: p.data.contract_name, number: p.data.contract_number, contract_date: p.data.contract_date })
        : ''),
    },
    { colId: 'organization', field: 'organization_name', headerName: 'Організація', width: 190, sortable: true },
    { colId: 'responsible', field: 'responsible_name', headerName: 'Відповідальний', width: 150, sortable: true },
    { colId: 'comment', field: 'comment', headerName: 'Коментар', width: 200, sortable: true },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [side]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>{cfg.title}<TitleTotal total={total} /></h2>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{cfg.subtitle}</span>
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <button className="primary" onClick={() => setShowCreate((s) => !s)}>+ створити ▾</button>
          {showCreate && (
            <div style={{
              position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 20,
              background: 'var(--bg-panel)', border: '1px solid var(--border-strong)', borderRadius: 6,
              boxShadow: '0 4px 12px var(--overlay)', minWidth: 270, overflow: 'hidden',
            }}>
              {cfg.create.map((o) => (
                <button key={o.to} onClick={() => { setShowCreate(false); navigate(o.to); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderRadius: 0,
                    background: 'none', color: 'var(--text)', padding: '0.5rem 0.75rem', cursor: 'pointer', fontWeight: 500 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-soft)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}>
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} />
        <ContractorFilter value={contractorId} onChange={setContractorId} label={cfg.contractorLabel} />
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Вид документа"
          title="Вид документа" style={{ width: 240, flex: '0 0 auto' }}>
          <option value="">Вид документа — усі</option>
          {kinds.map((k) => <option key={k.code} value={k.code}>{k.label}</option>)}
        </select>
        <input value={numberInput} onChange={(e) => setNumberInput(e.target.value)} placeholder="номер документа"
          aria-label="Номер документа" style={{ width: 160, flex: '0 0 auto' }} />
      </DocListFiltersRow>
      <InfiniteDocGrid<CommerceJournalRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey={cfg.storageKey}
        getRowId={(r) => `${r.kind}:${r.id}`}
        onRowClicked={showRelated}
        onRowDoubleClicked={open}
      />
      {related && (
        <RelatedDocsDialog
          doc={{ type: related.document_type, id: related.id }}
          onClose={() => setRelated(null)}
          subtitle={(
            <>
              <b>{related.kind_label}</b>
              {related.document_number ? ` № ${related.document_number}` : ''}
              {related.document_date ? ` від ${journalDay(related)}` : ''}
              {related.contractor_name ? ` · ${related.contractor_name}` : ''}
              {related.amount != null ? ` · ${fmtAmount(related.amount)} ${related.currency_code === 'UAH' ? 'грн' : related.currency_code}` : ''}
            </>
          )}
          actions={<button className="primary" onClick={() => open(related)}>відкрити документ</button>}
        />
      )}
    </div>
  );
}
